/**
 * The deployment's commercial tier.
 *
 * The application is single-tenant — one deployment per customer — so the plan
 * is a property of the installation, not of the signed-in user. It is read once
 * at startup from `GET /v1.0/subscription`.
 */
export type PlanTier = 'FREE' | 'PRO' | 'ENTERPRISE';

export type SubscriptionStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'EXPIRED';

/** Feature areas sold above the free tier. Mirrors the backend `PlanFeature` enum. */
export enum PlanFeature {
  /** Participation photos, the map view and the chronological trail. */
  VISUAL_JOURNAL = 'VISUAL_JOURNAL',
  MISSION_ORDERS = 'MISSION_ORDERS',
  EXPENSES = 'EXPENSES',
  /** Creating roles and editing the privileges attached to them. */
  ROLE_MANAGEMENT = 'ROLE_MANAGEMENT',
  /** The `require_geolocation` setting: refusing a punch that carries no position. */
  REQUIRED_GEOLOCATION = 'REQUIRED_GEOLOCATION',
  /** Supervisors and HR acting on their scope; without it, only administrator and employee. */
  TEAM_HIERARCHY = 'TEAM_HIERARCHY',
  /** The worked-hours reports and their CSV export. */
  REPORTS = 'REPORTS',
  /** Certificates written by the server. Requests themselves stay free. */
  GENERATED_CERTIFICATES = 'GENERATED_CERTIFICATES',
  /** Every export of the company's data. Imports stay free. */
  DATA_EXPORT = 'DATA_EXPORT',
}

export interface Subscription {
  plan: PlanTier;
  status: SubscriptionStatus;
  /** `null` when the plan does not expire. */
  validUntil: string | null;
  /** `null` when the plan is uncapped. */
  maxEmployees: number | null;
  currentEmployees: number;
  features: Partial<Record<PlanFeature, boolean>>;
}

/**
 * Used until the real subscription arrives, and kept if the call fails.
 *
 * Failing closed is deliberate: the server refuses gated endpoints on its own,
 * so the worst a wrong guess here can do is hide a menu until the next reload —
 * whereas failing open would show buttons that then error out.
 */
export const FREE_SUBSCRIPTION: Subscription = {
  plan: 'FREE',
  status: 'ACTIVE',
  validUntil: null,
  maxEmployees: 10,
  currentEmployees: 0,
  features: {
    [PlanFeature.VISUAL_JOURNAL]: false,
    [PlanFeature.MISSION_ORDERS]: false,
    [PlanFeature.EXPENSES]: false,
    [PlanFeature.ROLE_MANAGEMENT]: false,
    [PlanFeature.REQUIRED_GEOLOCATION]: false,
    [PlanFeature.TEAM_HIERARCHY]: false,
    [PlanFeature.REPORTS]: false,
    [PlanFeature.GENERATED_CERTIFICATES]: false,
    [PlanFeature.DATA_EXPORT]: false,
  },
};
